import { $apiV1, $apiV2 } from '../../config/api';

// ============================================================================
// Phase 2 billing endpoints
// ============================================================================

export const registerPlayer = (payload) =>
  $apiV2.post('/auth/register-player', payload);

export const resendVerification = (email) =>
  $apiV2.post('/auth/resend-verification', { email });

export const verifyEmailToken = (token) =>
  $apiV2.get(`/auth/verify-email/${encodeURIComponent(token)}`);

export const fetchSubscriptionPlans = () => $apiV2.get('/subscription/plans');

export const createSetupIntent = () =>
  $apiV2.post('/subscription/setup-intent');

export const startSelfServeTrial = (setupIntentId, priceLookupKey) =>
  $apiV2.post('/subscription/start-trial', {
    setup_intent_id: setupIntentId,
    ...(priceLookupKey ? { price_lookup_key: priceLookupKey } : {}),
  });

export const createCheckoutSession = (lookupKey) =>
  $apiV2.post('/subscription/checkout-session', { lookup_key: lookupKey });

export const createPortalSession = () =>
  $apiV2.post('/subscription/portal-session');

export const fetchSubscriptionStatus = () => $apiV2.get('/subscription/status');

// Reports Add-on PRD §5.3/§5.4/§5.6 — off-session item add, hosted bundle
// Checkout, soft cancel-at-period-end. 402 codes from the activate path
// bubble (see src/config/api.js) for the upgrade-sheet to surface.
export const activateReportsAddon = () =>
  $apiV2.post('/subscription/reports-addon');

export const createReportsAddonBundleCheckout = (returnUrl, plan = 'monthly') =>
  $apiV2.post('/subscription/reports-addon/bundle-checkout', {
    return_url: returnUrl,
    plan,
  });

export const cancelReportsAddon = () =>
  $apiV2.delete('/subscription/reports-addon');

// Referral system (SMRTSTATS-351). /me + /report are authed-player; /preview
// is public (no token) for the "X invited you" banner on the signup screen.
export const fetchReferralMe = () => $apiV2.get('/referral/me');

export const fetchReferralReport = () => $apiV2.get('/referral/report');

export const fetchReferralPreview = (code) =>
  $apiV2.get('/referral/preview', { params: { code } });

export const searchPlayerForOnboarding = (query, limit = 20) =>
  $apiV2.get('/player/onboarding/search', { params: { q: query, limit } });

export const mapSelfToPlayer = (playerId) =>
  $apiV2.post('/player/onboarding/map-self', { player_id: playerId });

// ============================================================================
// Existing player endpoints
// ============================================================================

// SMRTSTATS-312 #1: optional season + competition filters. Backend accepts
// `year_season_id` and a CSV `competition_ids` query param (Home page).
export const fetchPlayerDashboard = ({ seasonId, competitionIds } = {}) =>
  $apiV2.get('/player/dashboard', {
    params: {
      ...(seasonId ? { year_season_id: seasonId } : {}),
      ...(Array.isArray(competitionIds) && competitionIds.length > 0
        ? { competition_ids: competitionIds.join(',') }
        : {}),
    },
  });
export const fetchPlayerMatches = (params = {}) => $apiV2.get('/player/matches', { params });
export const fetchPlayerMatchDetail = (id) => $apiV2.get(`/player/matches/${id}`);
export const fetchPlayerStatistics = (params = {}) => $apiV2.get('/player/statistics', { params });
export const fetchPlayerSeasons = () => $apiV2.get('/player/seasons');
export const fetchPlayerVideos = (params = {}) => $apiV2.get('/player/videos', { params });
export const fetchPlayerProfile = () => $apiV2.get('/player/profile');

export const fetchPlayerOverview = (mappedPlayerId, seasonId) =>
  $apiV1.get(`/statistics/player_opt/${mappedPlayerId}`, {
    params: seasonId ? { year_season_id: seasonId } : {},
  });

export const fetchPlayerMatchesMeta = (mappedPlayerId) =>
  $apiV1.get(`/statistics/player_matches/${mappedPlayerId}`);

export const fetchPlayerMatchStats = (mappedPlayerId, seasonId, paramIds = []) =>
  $apiV2.get(`/platform/player/${mappedPlayerId}/matches`, {
    params: {
      year_season_id: seasonId,
      params_ids: Array.isArray(paramIds) ? paramIds.join(',') : paramIds,
    },
  });

export const fetchPlayerParameters = (group) =>
  $apiV1.get('/stats/parameters/player', {
    params: group ? { page_size: 1000, group } : { page_size: 1000 },
  });

export const checkPlayerAccess = (mappedPlayerId) =>
  $apiV2.get(`/access/check/${mappedPlayerId}/player`);

export const fetchMatchDetail = (matchId) =>
  $apiV1.get(`/platform/match/${matchId}`);

export const checkMatchVideoAccess = (matchId) =>
  $apiV2.get(`/access/match/${matchId}`);

export const fetchPlayerMarkers = (mappedPlayerId, paramId, opts = {}) =>
  $apiV2.get(`/platform/player/${mappedPlayerId}/markers`, {
    params: {
      param_id: paramId,
      ...(opts.matchId ? { match_id: opts.matchId } : {}),
      ...(opts.seasonId ? { year_season_id: opts.seasonId } : {}),
      per_page: opts.perPage ?? 200,
      // Backend caps per_page at 200 (verified on stage 2026-05-25). For
      // counts above that, callers paginate via this `page` param. Response
      // shape is DRF: { results: [...], count, next, previous }.
      ...(opts.page ? { page: opts.page } : {}),
    },
  });

export const fetchMarkersBulk = (markerIds, opts = {}) =>
  $apiV2.post('/player/markers_search', {
    markers: Array.isArray(markerIds) ? markerIds : [],
    to_merge: opts.merge ?? false,
    ...(opts.startMinute != null ? { start_minute: opts.startMinute } : {}),
    ...(opts.finishMinute != null ? { finish_minute: opts.finishMinute } : {}),
    ...(opts.bodyPartId ? { body_part_id: opts.bodyPartId } : {}),
  });

export const createVideoList = (markerIds, title) =>
  $apiV2.post('/player/video/list/create', {
    markers: Array.isArray(markerIds) ? markerIds.join(',') : String(markerIds),
    title,
    allMarkers: null,
  });

export const fetchVideoListByToken = (token) =>
  $apiV2.get(`/player/video/list/get/${token}`);

// SMRTSTATS-297 + SMRTSTATS-291: PWA player-report PDF download. The endpoint
// derives the player from the auth token (no playerId in URL) and the API
// returns the file as application/pdf, hence responseType: 'blob'.
export const downloadPlayerReportPdf = (yearSeasonId, competitionIds, opts = {}) =>
  $apiV2.post(
    '/player/platform/export/report/pdf',
    {
      year_season_id: Number(yearSeasonId),
      competition_ids: Array.isArray(competitionIds) ? competitionIds : [],
      ...(opts.date_from ? { date_from: opts.date_from } : {}),
      ...(opts.date_to ? { date_to: opts.date_to } : {}),
    },
    { responseType: 'blob' },
  );
